import { auth } from '../config/firebase';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export interface WaMessage {
  direction: 'in' | 'out';
  type: string;
  text: string;
  templateName?: string;
  status?: string;
  byAi?: boolean;
  at: string;
  // Inbound attachments and reactions — the thing itself, not a "[image]" placeholder.
  wamid?: string;
  mediaId?: string;
  mimeType?: string;
  filename?: string;
  caption?: string;
  reactionEmoji?: string;
  reactionTo?: string;
}

// Attachment bytes for one inbound message. Fetched with auth and handed back as an object URL,
// because an <img src> cannot carry the Firebase bearer token.
export async function fetchMediaObjectUrl(wamid: string): Promise<string> {
  const { data } = await axios.get(`${API_URL}/whatsapp/media/${encodeURIComponent(wamid)}`, {
    headers: await authHeaders(),
    responseType: 'blob',
  });
  return URL.createObjectURL(data as Blob);
}

export interface WaConversationSummary {
  phone: string;
  buyerName: string;
  platform: string;
  eventName: string;
  orderId: string;
  orderRef: string;
  orderIds?: string[];
  unread: number;
  aiMuted: boolean;
  awaitingOfferResponse: boolean;
  isIssue?: boolean;
  // Delivery gate: 'awaiting' = tickets held pending the buyer confirming they read the
  // instructions; deliveryForceAt is when they auto-send regardless.
  deliveryState?: 'awaiting' | 'approved' | 'delivered' | 'forced' | null;
  deliveryForceAt?: string | null;
  lastMessageAt: string;
  lastOutStatus?: 'sent' | 'delivered' | 'read' | 'failed' | null;
  lastOutAt?: string | null;
  withinWindow: boolean;
  lastMessage: { direction: 'in' | 'out'; text: string; type: string; at: string } | null;
}

export interface WaThread {
  phone: string;
  buyerName: string;
  platform: string;
  eventName: string;
  orderId: string;
  orderRef: string;
  orderIds?: string[];
  unread: number;
  aiMuted: boolean;
  awaitingOfferResponse: boolean;
  isIssue?: boolean;
  withinWindow: boolean;
  // Ticket delivery held behind the read-instructions gate (absent when none).
  pendingDelivery?: {
    state: 'awaiting' | 'approved' | 'delivered' | 'forced';
    links: string[];
    documents: { mediaId: string; filename: string }[];
    eventName: string;
    forceAt?: string;
    instructionsSentAt?: string;
    deliveredAt?: string;
  } | null;
  messages: WaMessage[];
}

export interface WaSettings {
  autoSendEnabled: boolean;
  enabledPlatforms: string[];
  templateName: string;
  templateLang: string;
  autoSendFrom?: string;
  agesAutoEnabled: boolean;
  agesHomeTeams: string[];
  agesEventKeywords: string[];
  helpBotEnabled: boolean;
  helpBotNumbers: string[];
  hasInstructionsPdf?: boolean;
  instructionsFilename?: string;
}

export async function listConversations(): Promise<WaConversationSummary[]> {
  const { data } = await axios.get(`${API_URL}/whatsapp/conversations`, {
    headers: await authHeaders(),
  });
  return data;
}

export interface OrderDetail {
  orderId: string;
  ref: string | null;
  event: string | null;
  category: string | null;
  quantity: number | null;
  orderDate: string | null;
  eventDate: string | null;
  totalPriceGBP: number | null; // converted — actual GBP on every platform
  totalNative: number | null; // amount in the platform's own currency
  currency: string | null;
  platform: string | null;
  boughtFrom: string | null;
  priceBought: number | null;
  profit: number | null;
  remarks: string | null;
  notes: string | null;
  agesNote: string | null;
  buyerName: string | null;
  buyerPhone: string | null;
}

export async function getConversationOrders(phone: string): Promise<{ orders: OrderDetail[] }> {
  const { data } = await axios.get(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/orders`,
    { headers: await authHeaders() },
  );
  return data;
}

export async function getThread(phone: string): Promise<WaThread> {
  const { data } = await axios.get(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}`,
    { headers: await authHeaders() },
  );
  return data;
}

export async function replyToConversation(phone: string, text: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/reply`,
    { text },
    { headers: await authHeaders() },
  );
  return data;
}

// Send the approved `attendee_ages` template to this buyer on demand (specific teams/events).
export async function requestAttendeeAges(phone: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/request-ages`,
    {},
    { headers: await authHeaders() },
  );
  return data;
}

// Chase an unanswered seat offer with the approved `offer_follow_up` template. A template because
// the offer was business-initiated, so there is usually no open 24-hour window to reply in.
export async function sendOfferFollowUp(phone: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/offer-follow-up`,
    {},
    { headers: await authHeaders() },
  );
  return data;
}

// Send a photo to the buyer as a manual inbox reply (24h window applies, like text replies).
// The current draft text can ride along as the image caption.
export async function sendPhotoReply(phone: string, file: File, caption?: string) {
  const form = new FormData();
  form.append('file', file);
  if (caption?.trim()) form.append('caption', caption.trim());
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/reply-photo`,
    form,
    { headers: await authHeaders(), timeout: 60000 },
  );
  return data;
}

// Send the approved `attendee_has_iphone` template — asks whether at least one person attending has
// an iPhone (buyer taps a Yes/No button). Works outside the 24-hour window (approved template).
export async function askHasIphone(phone: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/ask-iphone`,
    {},
    { headers: await authHeaders() },
  );
  return data;
}

// Bulk-send the "does at least one person have an iPhone?" template to a pasted list of ORDER ids
// (order id or Hello reference). Returns a per-id result (sent / failed / not found).
export interface BulkIphoneResult {
  total: number;
  sent: number;
  failed: number;
  results: { id: string; ok: boolean; buyer?: string; event?: string; error?: string }[];
}
// Ask which club the buyer supports (approved `supporter_club` template, names both teams).
export async function askSupporterClub(phone: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/ask-club`,
    {},
    { headers: await authHeaders() },
  );
  return data;
}

export async function askHasIphoneBulk(ids: string[]): Promise<BulkIphoneResult> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/ask-iphone-bulk`,
    { ids },
    { headers: await authHeaders() },
  );
  return data;
}

// "Your tickets are on the way" — match-day reassurance for buyers still waiting. Same bulk shape
// as askHasIphoneBulk: waiting buyers come in batches, not one at a time.
export async function sendTicketsComing(orderId: string): Promise<{ ok?: boolean; error?: string }> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/tickets-coming`,
    { orderId },
    { headers: await authHeaders() },
  );
  return data;
}
export async function sendTicketsComingBulk(ids: string[]): Promise<BulkIphoneResult> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/tickets-coming-bulk`,
    { ids },
    { headers: await authHeaders() },
  );
  return data;
}

// Offer the buyer different seats (better category/block, or a straight swap). Works outside the
// 24-hour window because it goes as an approved template, and flags the chat as awaiting a reply.
export async function offerSeats(
  phone: string,
  input: {
    template: 'seat_upgrade_offer' | 'seat_category_offer' | 'seat_category_split_offer';
    promo?: boolean;
    currentSplit?: string;
    offerSplit?: string;
    event: string;
    current: string;
    offer: string;
  },
) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/offer-seats`,
    input,
    { headers: await authHeaders() },
  );
  return data;
}

// ── Ticket delivery ──────────────────────────────────────────────────────────
// gate: hold tickets, send instructions template, release on their tap (or the failsafe).
// now: skip the gate and send immediately.
export async function deliverTickets(
  phone: string,
  input: {
    links: string;
    linkPairs?: { iphone?: string; android?: string }[];
    files: File[];
    mode: 'gate' | 'now' | 'both';
    oneTimeLinks?: boolean;
    replacement?: boolean; // earlier tickets cancelled by the club — sends the tickets_replaced notice instead of the instructions
    orderId?: string;      // which of the thread's orders this delivery is for (required when there are several)
  },
) {
  const form = new FormData();
  form.append('links', input.links);
  form.append('linkPairs', JSON.stringify(input.linkPairs || []));
  form.append('mode', input.mode);
  if (input.oneTimeLinks) form.append('oneTimeLinks', 'true');
  if (input.replacement) form.append('replacement', 'true');
  if (input.orderId) form.append('orderId', input.orderId);
  for (const f of input.files) form.append('files', f, f.name);
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/deliver`,
    form,
    { headers: await authHeaders() },
  );
  return data;
}

// The "just send the tickets" button on a delivery already waiting at the gate.
export async function releasePendingDelivery(phone: string) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/deliver/release`,
    {},
    { headers: await authHeaders() },
  );
  return data;
}

export async function uploadInstructionsPdf(file: File) {
  const form = new FormData();
  form.append('file', file, file.name);
  const { data } = await axios.post(`${API_URL}/whatsapp/settings/instructions-pdf`, form, {
    headers: await authHeaders(),
  });
  return data;
}

// orderId -> WhatsApp status. `label`/`code` are the detailed receipt state ("message seen",
// "instructions seen · not approved", "tickets seen"…); `stage`/`stageLabel` are the coarse
// delivery pipeline position — Processed / Awaiting approval / Delivered.
export async function getOrderWaStatuses(): Promise<
  Record<
    string,
    {
      code: string;
      label: string;
      stage: 'processed' | 'awaiting_approval' | 'delivered';
      stageLabel: string;
      delivered: boolean;
      receipt: 'sent' | 'delivered' | 'read' | 'failed' | null;
      receiptWarning: boolean;
    }
  >
> {
  const { data } = await axios.get(`${API_URL}/whatsapp/order-statuses`, { headers: await authHeaders() });
  return data;
}

// Same map for a specific set of orders — the Orders screen's "refresh statuses" button, which
// re-checks only what is currently on screen instead of reloading every order.
export async function getOrderWaStatusesFor(
  ids: string[],
): Promise<Awaited<ReturnType<typeof getOrderWaStatuses>>> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/order-statuses`,
    { ids },
    { headers: await authHeaders() },
  );
  return data;
}

// Mark an order delivered by hand — no proof, no messages. delivered:false undoes it.
export async function markOrderDelivered(orderId: string, delivered: boolean) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/orders/${encodeURIComponent(orderId)}/mark-delivered`,
    { delivered },
    { headers: await authHeaders() },
  );
  return data;
}

export async function setAiMuted(phone: string, muted: boolean): Promise<WaThread> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/mute-ai`,
    { muted },
    { headers: await authHeaders() },
  );
  return data;
}

export async function setAwaitingOfferResponse(phone: string, awaiting: boolean): Promise<WaThread> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/awaiting-offer`,
    { awaiting },
    { headers: await authHeaders() },
  );
  return data;
}

// Flag/unflag a chat as an issue — surfaced under the Messages "Issue" tab.
export async function setChatIssue(phone: string, issue: boolean): Promise<WaThread> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/conversations/${encodeURIComponent(phone)}/issue`,
    { issue },
    { headers: await authHeaders() },
  );
  return data;
}

export interface SentLinkHit {
  link: string;
  wallet: string;
  orderId: string;
  phone: string;
  eventName: string;
  buyerName: string;
  via: string;
  at: string;
}

// Search the permanent record of ticket links the system has sent/uploaded. q = full link or tail.
export async function searchSentLinks(q: string): Promise<SentLinkHit[]> {
  const { data } = await axios.get(`${API_URL}/whatsapp/link-search`, {
    params: { q },
    headers: await authHeaders(),
  });
  return data;
}

export async function getWaSettings(): Promise<WaSettings> {
  const { data } = await axios.get(`${API_URL}/whatsapp/settings`, {
    headers: await authHeaders(),
  });
  return data;
}

export async function updateWaSettings(patch: Partial<WaSettings>): Promise<WaSettings> {
  const { data } = await axios.patch(`${API_URL}/whatsapp/settings`, patch, {
    headers: await authHeaders(),
  });
  return data;
}

export interface OrderBrief {
  orderId: string;
  event: string;
  buyer: string;
  ages?: boolean;
}
export interface BackfillDryRun {
  dryRun: true;
  eligible: number;
  alsoGetAges: number;
  skippedNoPhone: number;
  skippedNoEventDate: number;
  totalLiveUnsentOrders: number;
  perRunCap: number;
  noPhoneOrders: OrderBrief[];
  sample: (OrderBrief & { eventDate: string | null })[];
  error?: string;
}
export interface BackfillResult {
  dryRun: false;
  sent: number;
  agesSent: number;
  failed: number;
  stoppedEarly: boolean;
  remaining: number;
  sentOrders: OrderBrief[];
  unusablePhoneOrders: OrderBrief[];
  failedOrders: OrderBrief[];
  noPhoneOrders: OrderBrief[];
  note: string;
  error?: string;
}

// One-off backfill of the confirmation (+ ages) to existing buyers on the selected platform(s) for
// upcoming matches. dryRun=true (default) only counts; dryRun=false actually sends.
export async function backfill(dryRun: boolean, platforms: string[], limit?: number) {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/backfill`,
    { dryRun, platforms, limit },
    { headers: await authHeaders() },
  );
  return data as BackfillDryRun | BackfillResult;
}

export interface TestSendResult {
  to?: string;
  template?: string;
  lang?: string;
  confirmation?: { accepted: boolean; wamid: string | null };
  ages?: { accepted: boolean; wamid: string | null };
  error?: string;
}

// Send both templates (confirmation + ages) to one number with dummy details, to verify on a test phone.
export async function sendTestTemplate(to: string): Promise<TestSendResult> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/test-template`,
    { to },
    { headers: await authHeaders() },
  );
  return data;
}

export interface AgesScanRow {
  phone: string;
  buyer: string;
  orderIds: string[];
  status: string;
  note?: string;
  reply?: string;
}
export interface AgesScanReport {
  conversationsWithAgesRequest: number;
  skippedAlreadyNoted: number;
  force: boolean;
  agesNoted: number;
  results: AgesScanRow[];
}

// Parse ages replies in chats that got the ages template; writes notes onto the orders.
// Incremental by default (already-noted chats are skipped — no AI calls); force=true re-extracts all.
export async function scanAges(force = false): Promise<AgesScanReport> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/scan-ages`,
    { force },
    { headers: await authHeaders() },
  );
  return data;
}

// Re-enable sending for specific order ids (clears whatsappSentAt) so they get messaged again.
export async function resetSent(orderIds: string[]): Promise<{ reset: number; orderIds?: string[] }> {
  const { data } = await axios.post(
    `${API_URL}/whatsapp/reset-sent`,
    { orderIds },
    { headers: await authHeaders() },
  );
  return data;
}
